import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kUntitledDraftName } from '@/drafting/constants'
import { kStaleDraftsTimestamp } from './slice'

/** A draft reduced to its artifact version id and display name. */
export interface ConversationDraft {
  artifactVersionId: string
  name: string
}

// Stable reference so the selector doesn't return a fresh array on every store update.
const kNoArtifactVersionIds: string[] = []
const kNoDrafts: ConversationDraft[] = []

/**
 * Select: a matter's draft artifact version ids, ordered most-recently-updated first.
 */
export const selectMatterDraftArtifactVersionIds = createSelector(
  [
    (state: RootState, props: { matterId: string | null }) =>
      props.matterId ? state.draftingState.draftArtifactVersionIdsByMatterId[props.matterId] : undefined,
    (state: RootState) => state.artifactsState.artifactsVersions,
  ],
  (ids, artifactsVersions): string[] => {
    if (!ids || ids.length === 0) return kNoArtifactVersionIds
    // Spread before sort — never mutate the stored array.
    return [...ids].sort((a, b) => (artifactsVersions[b]?.updated_at ?? 0) - (artifactsVersions[a]?.updated_at ?? 0))
  }
)

/**
 * Select: conversation id a draft's artifact version was created in, or null if unknown.
 */
export const selectConversationIdForDraftArtifactVersion = (state: RootState, props: { draftArtifactVersionId: string }): string | null => {
  return state.draftingState.conversationIdByDraftArtifactVersionId[props.draftArtifactVersionId] ?? null
}

/**
 * Select: a conversation's drafts.
 *
 * Order isn't set here: it follows selectMatterDraftArtifactVersionIds, so a re-sort there flows
 * through without touching this.
 */
export const selectDraftsForConversation = createSelector(
  [
    selectMatterDraftArtifactVersionIds,
    (state: RootState) => state.draftingState.conversationIdByDraftArtifactVersionId,
    (state: RootState) => state.artifactsState.artifactsVersions,
    (_state: RootState, props: { matterId: string | null; conversationId: string }) => props.conversationId,
  ],
  (matterDraftIds, conversationIdByDraftArtifactVersionId, artifactsVersions, conversationId): ConversationDraft[] => {
    const drafts = matterDraftIds
      .filter((id) => conversationIdByDraftArtifactVersionId[id] === conversationId)
      .map((id) => ({ artifactVersionId: id, name: artifactsVersions[id]?.name ?? kUntitledDraftName }))
    return drafts.length === 0 ? kNoDrafts : drafts
  }
)

/**
 * Select: whether a matter's drafts fetch is currently in flight.
 */
export const selectDraftsLoadingForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.loadingByMatterId[props.matterId] ?? false
}

/**
 * Select: whether a matter's last drafts fetch failed.
 */
export const selectDraftsFetchFailedForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.errorByMatterId[props.matterId] ?? false
}

/**
 * Select: whether a matter's drafts cache has been invalidated and needs refetching.
 */
export const selectDraftsStaleForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.lastRefreshByMatterId[props.matterId] === kStaleDraftsTimestamp
}

/**
 * Select: whether a matter's drafts initial load has completed (at least one fetch has finished).
 *
 * Lets the UI tell "not loaded yet" from "loaded and genuinely empty".
 */
export const selectDraftsInitialLoadCompleteForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.lastRefreshByMatterId[props.matterId] !== undefined
}
